import { error } from "@sveltejs/kit";
import { addBreadcrumb } from "@sentry/sveltekit";

export function gql(string: string) {
  return string.replace(/\s+/g, " ").trim();
}

type GraphQLError = { code: number; message: string };

function apiError(errors: Array<GraphQLError>) {
  // GraphQL errors will have a status of 200, but contain an error payload
  const status = errors.map((e) => e.code).pop() || 400;
  const messages = errors.map((e) => e.message).join(". ");

  error(status, `API Error: ${messages}`);
}

type ParamValue = string | number | boolean | Date | null;
type Parameters = { [key: string]: ParamValue };

function searchValue(value: ParamValue): string {
  if (value instanceof Date) {
    return value.toISOString();
  }

  return `${value}`;
}

function setSearchParams(url: URL, query: string, variables: Parameters) {
  url.searchParams.set("query", query);

  for (const [key, value] of Object.entries(variables)) {
    if (value != null) {
      url.searchParams.set(`variables[${key}]`, searchValue(value));
    }
  }

  url.searchParams.sort();

  return url;
}

type Fetch = (input: RequestInfo | URL, init?: RequestInit | undefined) => Promise<Response>;

type ClientRequest = {
  query: string;
  variables?: { [key: string]: ParamValue };
  url: URL;
};

export async function request(fetch: Fetch, { query, variables = {}, url }: ClientRequest) {
  if (url == null) {
    throw new Error("Configuration Error: request must provide origin url");
  }

  const endpoint = new URL("/api", url.origin);
  const hostname = url.hostname;

  setSearchParams(endpoint, query, variables);

  // Ensures proper caching between patches
  endpoint.searchParams.set("hostname", hostname);

  // Ensure stable sort for caching purposes
  endpoint.searchParams.sort();

  addBreadcrumb({
    category: "centcom.api",
    data: {
      hostname: hostname,
      request: `${endpoint}`
    }
  });

  const response = await fetch(endpoint, {
    headers: {
      "x-herald-origin": hostname
    }
  });

  if (response.ok == false) {
    return error(response.status, response.statusText);
  }

  const { errors, data } = await response.json();

  if (errors) {
    return apiError(errors);
  }

  return data;
}
